import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import "./Login.css";

const BOT_USERNAME = "terminalexbot";
const BOT_URL = `https://t.me/${BOT_USERNAME}`;

function formatApiError(detail) {
  if (!detail) return "Bir şeyler ters gitti";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return detail?.msg || String(detail);
}

export default function Login() {
  const nav = useNavigate();
  const { login, token } = useAuth();
  const [step, setStep] = useState("username");
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [successShow, setSuccessShow] = useState(false);
  const [successLog, setSuccessLog] = useState([]);
  const [userCheck, setUserCheck] = useState({ registered: null, status: null });

  const bgRef = useRef(null);
  const cardRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (token) nav("/", { replace: true });
  }, [token, nav]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  /* ===== Debounced "is this user registered?" check ===== */
  useEffect(() => {
    const uname = username.trim().toLowerCase().replace(/^@+/, "");
    if (uname.length < 2) {
      setUserCheck({ registered: null, status: null });
      return undefined;
    }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/auth/check-user`, {
          params: { username: uname },
          signal: ctl.signal,
        });
        setUserCheck({
          registered: !!data?.registered,
          status: data?.status || null,
        });
      } catch {
        /* sessiz — UX-only */
      }
    }, 450);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [username]);

  /* ===== Mouse-following background + 3D tilt + particles ===== */

  /* ===== Mouse-following background + 3D tilt + particles ===== */
  useEffect(() => {
    const bg = bgRef.current;
    const card = cardRef.current;
    const canvas = canvasRef.current;
    if (!bg || !card || !canvas) return;

    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let cx = tx;
    let cy = ty;
    let overCard = false;
    let rafLoop = 0;
    let rafDraw = 0;

    const onMove = (e) => { tx = e.clientX; ty = e.clientY; };
    const onEnter = () => { overCard = true; bg.classList.add("over"); };
    const onLeave = () => { overCard = false; bg.classList.remove("over"); };
    window.addEventListener("pointermove", onMove, { passive: true });
    card.addEventListener("pointerenter", onEnter);
    card.addEventListener("pointerleave", onLeave);

    const loop = () => {
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      bg.style.setProperty("--mx", cx + "px");
      bg.style.setProperty("--my", cy + "px");

      const r = card.getBoundingClientRect();
      const px = (cx - (r.left + r.width / 2)) / r.width;
      const py = (cy - (r.top + r.height / 2)) / r.height;
      const max = 6;
      const rx = (-py * max).toFixed(2);
      const ry = (px * max).toFixed(2);
      if (card) card.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg)`;

      rafLoop = requestAnimationFrame(loop);
    };
    rafLoop = requestAnimationFrame(loop);

    // particles
    const ctx = canvas.getContext("2d");
    let W = 0, H = 0, parts = [];
    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
      const count = Math.min(70, Math.floor((W * H) / 26000));
      parts = Array.from({ length: count }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.6 + 0.4,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of parts) {
        const dx = cx - p.x, dy = cy - p.y;
        const d2 = dx * dx + dy * dy;
        if (!overCard && d2 < 240 * 240) {
          const f = (1 - Math.sqrt(d2) / 240) * 0.06;
          p.vx += dx * 0.00006 * f * 100;
          p.vy += dy * 0.00006 * f * 100;
        }
        p.x += p.vx; p.y += p.vy;
        p.vx *= 0.96; p.vy *= 0.96;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;

        const dist = Math.sqrt(d2);
        const near = !overCard && dist < 240;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = near
          ? `rgba(255,93,21,${0.15 + (1 - dist / 240) * 0.5})`
          : "rgba(255,255,255,0.12)";
        ctx.fill();
      }
      rafDraw = requestAnimationFrame(draw);
    };
    rafDraw = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("resize", resize);
      card.removeEventListener("pointerenter", onEnter);
      card.removeEventListener("pointerleave", onLeave);
      cancelAnimationFrame(rafLoop);
      cancelAnimationFrame(rafDraw);
    };
  }, []);

  const requestToken = async (e) => {
    e?.preventDefault();
    if (!username.trim()) { toast.error("Kullanıcı adı gerekli"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/auth/request-token`,
        { username: username.trim() });
      if (data.sent === false) {
        toast.error("Kullanıcı bulunamadı. Önce Telegram'da /kayit yap.");
      } else {
        toast.success("Token Telegram'a gönderildi");
        setSecondsLeft(data.expires_in_seconds || 300);
        setStep("otp");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (!otp.trim()) { toast.error("Token gerekli"); return; }
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify`,
        { username: username.trim(), otp: otp.trim() });
      // Show success overlay + animate terminal log, THEN persist + navigate
      setSuccessShow(true);
      await runSuccessLog();
      login(data.token, data.user);
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
      setVerifying(false);
    }
  };

  const runSuccessLog = async () => {
    const lines = [
      { t: "> kimlik doğrulanıyor...", c: "" },
      { t: "> oturum anahtarı alındı", c: "ac" },
      { t: "> terminale bağlanıldı ✓", c: "ok" },
    ];
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    setSuccessLog([]);
    await sleep(1000); // wait for checkmark ring to draw
    const final = [];
    for (const item of lines) {
      let buf = "";
      final.push({ t: "", c: item.c });
      for (const ch of item.t) {
        buf += ch;
        final[final.length - 1] = { t: buf, c: item.c };
        setSuccessLog([...final]);
        await sleep(22);
      }
      await sleep(260);
    }
    await sleep(400);
  };

  return (
    <div className="dx-wrap">
      {/* ===== mouse-following background ===== */}
      <div className="dx-bg" ref={bgRef}>
        <div className="dx-grid" />
        <div className="dx-grid-hot" />
        <canvas className="dx-dots" ref={canvasRef} />
        <div className="dx-amb1" />
        <div className="dx-amb2" />
        <div className="dx-spot" />
        <div className="dx-vig" />
      </div>

      {/* ===== login card ===== */}
      <div className="dx-stage">
        <div className="dx-card" ref={cardRef} data-testid="login-card">
          <span className="dx-c3" /><span className="dx-c4" />

          <div className="dx-brand-row">
            <div className="dx-brand-mark">
              <svg viewBox="0 0 24 24"><path d="M13 2 4.5 13.5H11L9.5 22 19 9.5h-6.5L13 2z" /></svg>
            </div>
            <div className="dx-brand-id">
              <BrandLogo size="md" loop={true} eyebrow="Darkex Hedge" />
            </div>
          </div>

          <div className="dx-card-head">
            <div className="dx-session">
              {step === "username" ? "Oturum Başlat" : "Token Doğrula"}
            </div>
            <h2>{step === "username" ? "Terminale giriş yap" : "6 haneli kodu gir"}</h2>
            <p className="dx-sub">
              {step === "username"
                ? "Telegram hesabınla saniyeler içinde bağlan."
                : <>Token <b style={{ color: "var(--dx-accent-2)" }}>@{username}</b> hesabına Telegram&apos;dan gönderildi.</>
              }
            </p>
          </div>

          {step === "username" ? (
            <>
              <form onSubmit={requestToken}>
                <div className="dx-field">
                  <label htmlFor="tg-user">Telegram Kullanıcı Adı</label>
                  <div className="dx-input-wrap">
                    <input
                      id="tg-user"
                      data-testid="login-username"
                      type="text"
                      placeholder="kullanici_adi"
                      autoComplete="off"
                      spellCheck="false"
                      autoFocus
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                    <span className="dx-at">@</span>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="login-send-token"
                  disabled={sending}
                  className={`dx-btn dx-btn-primary ${sending ? "loading" : ""}`}
                >
                  <span className="dx-btn-content">
                    <svg viewBox="0 0 24 24"><path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" /></svg>
                    <span>Giriş Kodu Yolla</span>
                  </span>
                  <span className="dx-btn-spinner" aria-hidden="true" />
                </button>
              </form>

              {userCheck.registered === true ? (
                <div className="dx-note dx-note-ok" data-testid="login-user-recognized">
                  <span className="dx-note-icon">✓</span>
                  <p className="dx-note-tx">
                    <b style={{ color: "var(--dx-accent-2)" }}>@{username.trim().replace(/^@+/, "")}</b>
                    {" "}tanındı. Telegram&apos;a göndereceğimiz 6 haneli kodla giriş yap.
                  </p>
                </div>
              ) : (
                <>
                  <div className="dx-divider">hesabın yok mu?</div>

                  <a
                    href={BOT_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="connect-telegram-btn"
                    className="dx-btn dx-btn-tg"
                  >
                    <svg viewBox="0 0 24 24"><path d="M21.9 4.6 18.8 19c-.2 1-.8 1.2-1.7.8l-4.6-3.4-2.2 2.1c-.3.3-.5.5-1 .5l.4-4.9L18.6 6c.4-.3-.1-.5-.6-.2L7 12.5l-4.7-1.5c-1-.3-1-1 .2-1.5L20.6 3.3c.8-.3 1.6.2 1.3 1.3z" /></svg>
                    Telegram ile Kayıt Oluştur
                  </a>

                  <div className="dx-note" data-testid="login-signup-hint">
                    <span className="dx-note-icon">i</span>
                    <p className="dx-note-tx">
                      Henüz kayıtlı değil misin? Telegram&apos;da{" "}
                      <a href={BOT_URL} target="_blank" rel="noreferrer">@{BOT_USERNAME}</a>
                      {" "}aratıp <code>/kayit</code> yaz. Kullanıcı adın otomatik olarak hesabın olur.
                    </p>
                  </div>
                </>
              )}
            </>
          ) : (
            <form onSubmit={verify}>
              <div className="dx-otp-banner">
                <span className="dx-dot" /> Token gönderildi
                {secondsLeft > 0 && <span className="dx-otp-timer"> · {secondsLeft}s</span>}
              </div>

              <div className="dx-field">
                <label htmlFor="otp">Doğrulama Kodu</label>
                <div className="dx-input-wrap">
                  <input
                    id="otp"
                    data-testid="login-otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    autoFocus
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="dx-otp-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="login-verify"
                disabled={verifying || otp.length !== 6}
                className={`dx-btn dx-btn-primary ${verifying ? "loading" : ""}`}
              >
                <span className="dx-btn-content">
                  <svg viewBox="0 0 24 24"><path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" /></svg>
                  <span>Giriş Yap</span>
                </span>
                <span className="dx-btn-spinner" aria-hidden="true" />
              </button>

              <button
                type="button"
                data-testid="login-back"
                onClick={() => { setStep("username"); setOtp(""); }}
                className="dx-back-link"
              >
                ← Kullanıcı adını değiştir
              </button>
            </form>
          )}

          <div className="dx-card-foot">
            <span className="dx-secure">
              <svg viewBox="0 0 24 24"><path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" /></svg>
              Şifreli Oturum
            </span>
            <span>Developed by <b>PARANOYA</b></span>
          </div>

          {/* ===== success overlay ===== */}
          <div
            className={`dx-success ${successShow ? "show" : ""}`}
            aria-hidden={!successShow}
            data-testid="login-success-overlay"
          >
            <div className="dx-success-inner">
              <div className="dx-check-ring">
                <svg viewBox="0 0 80 80">
                  <circle className="dx-ring" cx="40" cy="40" r="36" />
                  <path className="dx-tick" d="M24 41 L35 52 L57 29" />
                </svg>
              </div>
              <h3 className="dx-success-title">Giriş başarılı</h3>
              <div className="dx-term-log">
                {successLog.map((ln, i) => (
                  <span key={i} className={`dx-ln ${ln.c === "ok" ? "dx-ok" : ln.c === "ac" ? "dx-ac" : ""}`}>
                    {ln.t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
